import { Arr } from '@deqo/foundation';
import {
    arrow,
    autoUpdate,
    flip,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole
} from '@floating-ui/react-dom-interactions';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Colors, remInPx } from '.';
import HelpTipIcon from '../@resources/img/HelpTip.png';
import { LFC } from '../Legacy';
import { PopArrow } from './PopArrow';

const tipIn = keyframes`
    from { opacity: 0 }
    to { opacity: 1 }
`;

export const TipReference = styled.div<{ block?: boolean }>`
    display: ${p => p.block ? 'block' : 'inline-flex'};
    vertical-align: top;
`;

export const TipFrame = styled.div<{ invert?: boolean, instant?: boolean }>`
    position: relative;
    z-index: 1000;
    padding: .25rem .5rem;
    font-weight: 500;
    font-size: .875rem;
    background: ${Colors.primaryText};
    color: white;
    border-radius: .25rem;
    text-align: center;
    width: max-content;
    max-width: 32rem;
    white-space: pre-wrap;

    ${p => p.instant ? '' : css`
        opacity: 0;
        animation: .15s ${tipIn} .3s ease-out;
        animation-fill-mode: forwards;
    `}

    ${p => p.invert ? css`
        background: white;
        color: ${Colors.primaryText};
    ` : ''}
`;

export type TipProps = {
    text: string | string[],
    position?: Placement,
    children: JSX.Element,
    block?: boolean,
    invert?: boolean,
    disabled?: boolean,
    rest?: number,
};

const tipArrow = (position: Placement, left: any, top: any) => {
    return ({
        position,
        style: { left, top }
    });
};

export const Tip = ({ children, text, position, invert, disabled, block, rest }: TipProps) => {
    text = (Array.isArray(text) ? text : [text]).map(t => t?.trim()).filter(t => !!t);

    const [open, setOpen] = useState(null as boolean);
    const [index, setIndex] = useState(0);
    const textNow = Arr.at(text, index)?.replace(/  /g, ' ').replace(/\s?\n\s?/g, '\n');

    const arrowRef = useRef<HTMLDivElement>();

    const {
        x,
        y,
        placement,
        reference,
        floating,
        strategy,
        context,
        refs,
        update,
        middlewareData: { arrow: { x: arrowX = 0, y: arrowY = 0 } = {} }
    } = useFloating({
        placement: position || 'top',
        onOpenChange: setOpen,
        middleware: [offset(remInPx(.5)), flip(), shift({ padding: remInPx(.5) }), arrow({ element: arrowRef })]
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, { restMs: rest || 250 }),
        useFocus(context),
        useRole(context, { role: 'tooltip' }),
        useDismiss(context)
    ]);

    useEffect(() => {
        if (refs.reference.current && refs.floating.current && open) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
    }, [refs.reference, refs.floating, update, open]);

    useEffect(() => {
        if (open === false) {
            setIndex(i => i + 1);
        }
    }, [open]);

    return (
        <>
            <TipReference block={block} {...getReferenceProps({ ref: reference })}>
                {children}
            </TipReference>
            {!!open && !!textNow && !disabled && <>
                <TipFrame invert={invert}
                    {...getFloatingProps({
                        ref: floating,
                        style: {
                            position: strategy,
                            top: y ?? '',
                            left: x ?? ''
                        }
                    })}
                >
                    {textNow}
                    <PopArrow ref={arrowRef} {...tipArrow(placement, arrowX, arrowY)} color={invert ? '#fff' : Colors.primaryText} />
                </TipFrame>
            </>}
        </>
    );
};

export const ClickTip = ({ children, text, position, disabled }: TipProps) => {
    text = (Array.isArray(text) ? text : [text]).map(t => t?.trim()).filter(t => !!t);

    const [open, setOpen] = useState(null as boolean);
    const [index, setIndex] = useState(0);
    const textNow = Arr.at(text, index)?.replace(/  /g, ' ').replace(/\s?\n\s?/g, '\n');
    const arrowRef = useRef<HTMLDivElement>();

    const {
        x,
        y,
        placement,
        reference,
        floating,
        strategy,
        context,
        refs,
        update,
        middlewareData: { arrow: { x: arrowX = 0, y: arrowY = 0 } = {} }
    } = useFloating({
        placement: position || 'top',
        open,
        onOpenChange: setOpen,
        middleware: [offset(remInPx(.5)), flip(), shift({ padding: remInPx(.5) }), arrow({ element: arrowRef })]
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'tooltip' }),
        useDismiss(context)
    ]);

    useEffect(() => {
        if (refs.reference.current && refs.floating.current && open) {
            return autoUpdate(refs.reference.current, refs.floating.current, update);
        }
    }, [refs.reference, refs.floating, update, open]);

    useEffect(() => {
        if (open === false) {
            setIndex(i => i + 1);
        }
    }, [open]);

    return (
        <>
            <TipReference {...getReferenceProps({ ref: reference })}>
                {children}
            </TipReference>
            {!!open && !!textNow && !disabled && <>
                <TipFrame instant
                    {...getFloatingProps({
                        ref: floating,
                        style: {
                            position: strategy,
                            top: y ?? '',
                            left: x ?? ''
                        }
                    })}
                >
                    {textNow}
                    <PopArrow ref={arrowRef} {...tipArrow(placement, arrowX, arrowY)} />
                </TipFrame>
            </>}
        </>
    );
};

const HelpFrame = styled.div`
    position: relative;
    display: inline-block;
    vertical-align: text-bottom;
    height: 1em;
    width: 1rem;
    margin: 0 .25em .0675em;
    user-select: none;
`;

const HelpIcon = styled.div`
    background: url(${HelpTipIcon});
    background-size: contain;
    color: #fff;
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    user-select: none;
`;

const Base: LFC<{ content: string }> = ({ content }) => {
    // return <Tooltip
    //     style={{ display: 'inline-flex' }}
    //     arrow={true}
    //     animation="fade"
    //     animateFill={false}
    //     duration={150}
    //     title={content}
    //     position="top"
    //     delay={[0, 0] as any}
    //     size="small"
    //     trigger="click">
    //     <Icon />
    // </Tooltip>;

    return <HelpFrame>
        <ClickTip text={content} position="top">
            <HelpIcon />
        </ClickTip>
    </HelpFrame>;
};

export const HelpTip = Object.assign(Base, { Frame: HelpFrame, Icon: HelpIcon });

